import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import {
  findObjectUseCase,
  upsertUseCase,
} from "../../../../../usecases/object";
import { auditLogUseCase } from "../../../../../usecases/logs";
import {
  addDaysISO,
  createBusinessDayRange,
  generateAuditMessage,
  getBusinessDate,
  isAfterFiveAMManila,
  toISODate,
  toNumber,
} from "./utils/helperFunc";

class InventoryFormPresenter extends BaseFormPresenter {
  componentDidMount() {
    this.init();
    this.getObject();
    this.fetchInfiniteItems();
    this.getUserLoggedIn();
    this.loadCategories();
  }

  onChange(value, field) {
    this.change[field] = value;
    this.object[field] = value;

    if (!this.object.raw_materials && this.view.state.object?.raw_materials) {
      this.object.raw_materials = [...this.view.state.object.raw_materials];
    }

    this.view.setObject(this.object);
  }

  searchSubmit(search) {
    this.reset();
    this.search = search;
    this.getObjects();
  }

  async getUserLoggedIn() {
    const userLoggedIn = this.view.getCurrentUser();
    const query = {
      where: {
        id: userLoggedIn?.id,
      },
      include: ["storeAnalytic", "storeAnalytics"],
    };
    const user = await findObjectUseCase().execute("users", query);

    this.userLoggedIn = user;
  }

  async loadCategories() {
    try {
      const categories = await findObjectUseCase().execute("category", {
        limit: 1000,
        sort: { name: 1 },
      });

      this.view.setState({
        categories: categories.map((cat) => ({
          value: cat.id,
          label: cat.name,
        })),
      });
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = {
        include: ["raw_materials", "raw_materials.uom", "uom", "category"],
      };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async fetchInfiniteItems(isInitial = false, keyword = "", categoryId = null) {
    this.view.setState({ isLoading: true });

    if (isInitial) {
      this.view.setState({
        skipRaw: 0,
        skipComp: 0,
        infiniteItems: [],
        hasMore: true,
      });
    }

    const skipRaw = isInitial ? 0 : (this.view.state.skipRaw ?? 0);
    const skipComp = isInitial ? 0 : (this.view.state.skipComp ?? 0);

    // shared where clause
    const whereClause = {};
    if (keyword) whereClause.itemName = { $regex: keyword, $options: "i" };
    if (categoryId) whereClause.category = { id: categoryId };

    const limit = 10;

    const [raws, comps] = await Promise.all([
      findObjectUseCase().execute("raw_materials", {
        where: whereClause,
        // limit,
        include: ["uom", "category"],
        skip: skipRaw,
      }),
      findObjectUseCase().execute("components", {
        where: whereClause,
        // limit,
        include: ["uom", "category"],
        skip: skipComp,
      }),
    ]);

    const taggedRaws = (raws || []).map((x) => ({
      ...x,
      source: "raw_materials",
    }));
    const taggedComps = (comps || []).map((x) => ({
      ...x,
      source: "components",
    }));

    const merged = [...taggedRaws, ...taggedComps].sort((a, b) =>
      String(a.itemName || "").localeCompare(
        String(b.itemName || ""),
        undefined,
        { sensitivity: "base" },
      ),
    );

    this.view.setState((prev) => ({
      infiniteItems: isInitial ? merged : [...prev.infiniteItems, ...merged],
      skipRaw: skipRaw + (raws?.length || 0),
      skipComp: skipComp + (comps?.length || 0),
      hasMore: raws?.length === limit || comps?.length === limit,
      isLoading: false,
    }));
  }

  handleInfiniteScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    const { hasMore, isLoading } = this.view.state;

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      hasMore &&
      !isLoading
    ) {
      this.fetchInfiniteItems();
    }
  };

  async createInventoryLogs(data) {
    const { editedItems, existingEditedRawMaterials, isUpdate, user, storeId } =
      data;

    const editedItemsArray = Object.values(editedItems);

    const inventoryLogPromises = editedItemsArray.map(async (item) => {
      const oldQuantity = isUpdate
        ? existingEditedRawMaterials[item.id]?.quantity ||
          item.stock_quantity ||
          "0"
        : item.stock_quantity || "0";
      const newQuantity = item.quantity || "0";

      const logEntry = {
        user: { id: user.id },
        itemName: item.itemName,
        adjustmentType: "Partial Count",
        oldQuantity: String(oldQuantity),
        newQuantity: String(newQuantity),
        storeId: { id: storeId },
      };

      return await this.upsertUseCase.execute("inventory_logs", logEntry);
    });

    await Promise.all(inventoryLogPromises);
  }

  async createAuditLog(data) {
    const {
      user,
      auditMessage,
      collection,
      responseId,
      documentId,
      existingObject,
      response,
      payload,
      storeId,
      isUpdate,
    } = data;

    await auditLogUseCase().create({
      user: { id: user.id },
      logDate: new Date().toISOString(),
      message: auditMessage,
      collection,
      objectId: responseId || documentId,
      payload: {
        before: isUpdate ? existingObject : null,
        after: response || payload,
      },
      store: { id: storeId },
    });
  }

  buildActualCountPayload(data) {
    const {
      actualCountDate,
      infiniteItems,
      existingEditedRawMaterials,
      editedItems,
      user,
      storeId,
      documentId,
    } = data;

    const dateOnly = new Date(actualCountDate).toISOString().split("T")[0];

    const mergedEditedRawMaterials = {
      ...existingEditedRawMaterials,
      ...editedItems,
    };

    const editedRawArray = Object.values(editedItems).map((item) => ({
      sku: item.sku,
      quantity: item.quantity,
    }));

    const payload = {
      actualDate: dateOnly,
      // raw_materials: infiniteItems,
      // edited_raw_materials: mergedEditedRawMaterials,
      edited_raw: editedRawArray,
      adjustedBy: { id: user.id },
      storeId: { id: storeId },
      status: "Partial",
    };

    if (documentId) {
      payload.id = documentId;
    }

    return payload;
  }

  async checkDuplicateActualCount(storeId, actualCountDate) {
    const { start, end } = createBusinessDayRange(actualCountDate);

    const query = {
      where: {
        actualDate: {
          $gte: start,
          $lte: end,
        },
        "storeId.id": storeId,
      },
    };

    console.log("Duplicate check query:", query);

    const existingRecords = await findObjectUseCase().execute(
      "actual_count_logs",
      query,
    );

    return existingRecords && existingRecords.length > 0;
  }

  extractViewData() {
    const user = this.view.getCurrentUser();
    const storeId = user?.storeAnalytic?.[0]?.id;
    const actualCountDate = this.view.state.actualCountDate;
    const editedItems = this.view.state.selectedQuantities || {};
    const infiniteItems = this.view.state.infiniteItems || [];
    const existingObject = this.view.state.object || {};
    const existingEditedRawMaterials =
      existingObject.edited_raw_materials || {};
    const documentId = existingObject.id;

    return {
      user,
      storeId,
      actualCountDate,
      editedItems,
      infiniteItems,
      existingObject,
      existingEditedRawMaterials,
      documentId,
    };
  }

  async saveActual() {
    const collection = "actual_count_logs";

    // Extract all data from view
    const viewData = this.extractViewData();
    const {
      user,
      storeId,
      actualCountDate,
      editedItems,
      infiniteItems,
      existingObject,
      existingEditedRawMaterials,
      documentId,
    } = viewData;

    const isUpdate = !!documentId;

    // Log extracted data
    console.log("Operation:", isUpdate ? "UPDATE" : "CREATE");
    console.log("Document ID:", documentId);
    console.log("Store ID:", storeId);
    console.log("Actual Count Date:", actualCountDate);

    // Check for duplicates (only for new records)
    if (!isUpdate) {
      try {
        const isDuplicate = await this.checkDuplicateActualCount(
          storeId,
          actualCountDate,
        );

        if (isDuplicate) {
          this.view.showError(
            "An actual count for this business day already exists.",
          );
          return;
        }
      } catch (error) {
        console.error("Error checking existing actualDate:", error);
        this.view.showError("Failed to verify date. Please try again.");
        return;
      }
    }

    // Build payload
    const payload = this.buildActualCountPayload({
      actualCountDate,
      infiniteItems,
      existingEditedRawMaterials,
      editedItems,
      user,
      storeId,
      documentId,
    });

    console.log("Payload:", payload);

    // Save to database
    this.view.showProgressDialog();
    try {
      const response = await this.upsertUseCase.execute(collection, payload);

      // Generate audit message
      const auditMessage = generateAuditMessage(
        editedItems,
        existingEditedRawMaterials,
        isUpdate,
      );

      // Create audit log
      await this.createAuditLog({
        user,
        auditMessage,
        collection,
        responseId: response.id,
        documentId,
        existingObject,
        response,
        payload,
        storeId,
        isUpdate,
      });

      // Create inventory logs
      await this.createInventoryLogs({
        editedItems,
        existingEditedRawMaterials,
        isUpdate,
        user,
        storeId,
      });

      // Show success
      this.view.hideProgressDialog();
      this.view.navigateBack();
      this.showSuccessDialog();
    } catch (error) {
      this.view.hideProgressDialog();
      console.error("Error saving actual count:", error);
      this.view.showError("Failed to save actual count. Please try again.");
    }
  }

  showSuccessDialog() {
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Partial Count Saved"
          message="This record is currently stored in Partial Count Logs and is awaiting confirmation.

You may edit or update this entry anytime from the Partial Count Logs module before final confirmation."
          isCancelButton={false}
          onSubmit={() => dialog.close()}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  //   async saveActual() {
  //     const collection = "actual_count_logs";
  //     const user = this.view.getCurrentUser();
  //     const storeId = user?.storeAnalytic?.[0]?.id;
  //     const actualCountDate = this.view.state.actualCountDate;
  //     const editedItems = this.view.state.selectedQuantities || {};
  //     const infiniteItems = this.view.state.infiniteItems || [];

  //     // Get existing object (if updating)
  //     const existingObject = this.view.state.object || {};
  //     const existingEditedRawMaterials =
  //       existingObject.edited_raw_materials || {};
  //     const documentId = existingObject.id;

  //     const dateOnly = new Date(actualCountDate).toISOString().split("T")[0];
  //     console.log("Date Only: ", dateOnly); // "2025-12-03"

  //     console.log("Document ID: ", documentId);
  //     console.log("Adjusted By: ", user);
  //     console.log("Actual Count Date: ", dateOnly); // ← Now shows date only
  //     console.log("Edited: ", editedItems);
  //     console.log("Raw Materials: ", infiniteItems);
  //     console.log("Existing Edited Raw Materials: ", existingEditedRawMaterials);

  //     // ============================================================
  //     // CHECK IF actualDate ALREADY EXISTS (for new records only)
  //     // ============================================================
  //     if (!documentId) {
  //       try {
  //         // Parse the selected date
  //         const selectedDate = new Date(actualCountDate);

  //         // Create the 5:00 AM start time for this date
  //         const startOfBusinessDay = new Date(selectedDate);
  //         startOfBusinessDay.setHours(5, 0, 0, 0);

  //         // Create the 4:59 AM end time for next day
  //         const endOfBusinessDay = new Date(selectedDate);
  //         endOfBusinessDay.setDate(endOfBusinessDay.getDate() + 1);
  //         endOfBusinessDay.setHours(4, 59, 59, 999);

  //         // Convert to ISO strings
  //         const startISO = startOfBusinessDay.toISOString();
  //         const endISO = endOfBusinessDay.toISOString();

  //         console.log("Business Day Range:", startISO, "to", endISO);
  //         // Example output: "2025-12-03T05:00:00.000Z" to "2025-12-04T04:59:59.999Z"

  //         const query = {
  //           where: {
  //             actualDate: {
  //               $gte: startISO, // From 5:00 AM today
  //               $lte: endISO, // Until 4:59 AM tomorrow
  //             },
  //             "storeId.id": storeId,
  //           },
  //         };
  //         console.log("qq", query);

  //         const existingRecords = await findObjectUseCase().execute(
  //           collection,
  //           query,
  //         );

  //         if (existingRecords && existingRecords.length > 0) {
  //           this.view.showError(
  //             "An actual count for this business day already exists.",
  //           );
  //           return;
  //         }
  //       } catch (error) {
  //         console.error("Error checking existing actualDate:", error);
  //         this.view.showError("Failed to verify date. Please try again.");
  //         return;
  //       }
  //     }
  //     // ============================================================

  //     // Merge existing edited_raw_materials with new edits
  //     const mergedEditedRawMaterials = {
  //       ...existingEditedRawMaterials,
  //       ...editedItems,
  //     };

  //     // Build payload
  //     const payload = {
  //       actualDate: dateOnly, // ← Save date only (YYYY-MM-DD)
  //       raw_materials: infiniteItems,
  //       edited_raw_materials: mergedEditedRawMaterials,
  //       adjustedBy: { id: user.id },
  //       storeId: { id: storeId },
  //       status: "Partial",
  //     };

  //     // Only include ID if updating existing record
  //     if (documentId) {
  //       payload.id = documentId;
  //     }

  //     console.log("Payload: ", payload);
  //     console.log("Operation: ", documentId ? "UPDATE" : "CREATE");

  //     this.view.showProgressDialog();
  //     try {
  //       const response = await this.upsertUseCase.execute(collection, payload);
  //       const nowISO = new Date().toISOString();
  //       const isUpdate = !!documentId;

  //       // Build audit log message
  //       let auditMessage = "";
  //       const editedItemsArray = Object.values(editedItems);

  //       if (isUpdate) {
  //         const changedItems = editedItemsArray
  //           .map((item) => {
  //             const oldQty =
  //               existingEditedRawMaterials[item.id]?.quantity ||
  //               item.stock_quantity ||
  //               "0";
  //             const newQty = item.quantity || "0";
  //             return `${item.itemName}: ${oldQty} → ${newQty}`;
  //           })
  //           .join(", ");

  //         auditMessage = `Actual Count Updated: ${changedItems}`;
  //       } else {
  //         const itemCount = editedItemsArray.length;
  //         const itemNames = editedItemsArray
  //           .slice(0, 3)
  //           .map((i) => i.itemName)
  //           .join(", ");
  //         auditMessage = `Actual Count Created: ${itemCount} item(s) adjusted${
  //           itemCount > 3 ? ` (${itemNames}...)` : ` (${itemNames})`
  //         }`;
  //       }

  //       await auditLogUseCase().create({
  //         user: { id: user.id },
  //         logDate: nowISO,
  //         message: auditMessage,
  //         collection,
  //         objectId: response.id || documentId,
  //         payload: {
  //           before: isUpdate ? existingObject : null,
  //           after: response || payload,
  //         },
  //         store: { id: storeId },
  //       });

  //       // Create inventory logs
  //       const inventoryLogPromises = editedItemsArray.map(async (item) => {
  //         const oldQuantity = isUpdate
  //           ? existingEditedRawMaterials[item.id]?.quantity ||
  //             item.stock_quantity ||
  //             "0"
  //           : item.stock_quantity || "0";
  //         const newQuantity = item.quantity || "0";

  //         const logEntry = {
  //           user: { id: user.id },
  //           itemName: item.itemName,
  //           adjustmentType: "Partial Count",
  //           oldQuantity: String(oldQuantity),
  //           newQuantity: String(newQuantity),
  //           storeId: { id: storeId },
  //         };

  //         return await this.upsertUseCase.execute("inventory_logs", logEntry);
  //       });

  //       await Promise.all(inventoryLogPromises);

  //       this.view.hideProgressDialog();
  //       this.view.navigateBack();

  //       dialog.fire({
  //         html: (
  //           <DialogModal
  //             icon="bi bi-question-octagon-fill"
  //             iconColor="#82c14a"
  //             title="Partial Count Saved"
  //             message="This record is currently stored in Partial Count Logs and is awaiting confirmation.

  // You may edit or update this entry anytime from the Partial Count Logs module before final confirmation.
  // "
  //             isCancelButton={false}
  //             onSubmit={() => {
  //               dialog.close();
  //             }}
  //             onClose={() => dialog.close()}
  //           />
  //         ),
  //         footer: false,
  //       });
  //     } catch (error) {
  //       this.view.hideProgressDialog();
  //       console.error("Error saving actual count:", error);
  //     }
  //   }

  // async save() {
  //   this.view.showProgressDialog();
  //   const collection = this.view.getCollectionName();
  //   const user = this.view.getCurrentUser();
  //   const adjustmentType = this.view.getAdjustmentType();
  //   const now = new Date();
  //   const nowISO = now.toISOString();

  //   const editedItems = this.view.state.selectedQuantities || {};
  //   const infiniteItems = this.view.state.infiniteItems || [];

  //   // helpers
  //   const day = nowISO.slice(0, 10); // 'YYYY-MM-DD'
  //   const storeId = user?.storeAnalytic?.[0]?.id;
  //   const storeCode = user?.storeAnalytic?.[0]?.code;
  //   const afterFive = isAfterFiveAMManila(now);

  //   const operationType =
  //     this.userLoggedIn[0]?.storeAnalytic?.[0]?.operationType;

  //   const is24_7 = operationType === "24/7 Store";

  //   const editedFinals = new Map();

  //   const ids = Object.keys(editedItems);
  //   const total = ids.length;
  //   this.view.showSaveProgress(total, `Saving ${total} items...`);
  //   let done = 0;

  //   try {
  //     // PASS #1
  //     for (const id in editedItems) {
  //       const edited = editedItems[id];
  //       const found = infiniteItems.find((item) => item.id === id);
  //       const quantity = parseFloat(edited.quantity || "0");
  //       const itemName = edited.itemName || found?.itemName;
  //       const itemSku = edited?.sku || found?.sku;
  //       const cost = edited.cost || found?.cost || 0;

  //       console.log("Found: ", found, "Edited: ", edited);
  //       // fetch existing inventory for this item+store
  //       const [existing] = await findObjectUseCase().execute("inventory", {
  //         where: {
  //           sku: { $eq: itemSku },
  //           "storeId.id": { $eq: storeId },
  //         },
  //         limit: 1,
  //       });

  //       const currentCount = parseFloat(existing?.systemCount || "0");

  //       let systemCount = currentCount; // default: keep as-is
  //       let actualCount = null;

  //       switch (adjustmentType) {
  //         case "Manual Add":
  //           systemCount = currentCount + quantity;
  //           break;

  //         case "Manual Deduct":
  //           systemCount = currentCount - quantity;
  //           break;

  //         case "Actual Count": {
  //           // Always record physical count
  //           actualCount = quantity;

  //           // Determine store operation type

  //           if (afterFive) {
  //             // --- AFTER 5AM LOGIC ---
  //             if (is24_7) {
  //               // 24/7 Store → ALWAYS reconcile after 5 AM
  //               if (currentCount < 0) {
  //                 // systemCount = actualCount + currentCount; // offset deficit
  //                 actualCount = actualCount;
  //               } else {
  //                 // systemCount = actualCount; // normal reconcile
  //                 actualCount = actualCount;
  //               }
  //             } else {
  //               // Daily Store → after 5 AM, SAVE actual only, do NOT touch systemCount
  //               actualCount = currentCount; // keep system
  //             }
  //           } else {
  //             // --- BEFORE 5AM LOGIC (same as original) ---
  //             systemCount = currentCount;
  //           }

  //           break;
  //         }

  //         case "Beginning Inventory":
  //         default:
  //           systemCount = quantity;
  //           break;
  //       }

  //       // Ensure numeric -> string as per your payload
  //       const payload = {
  //         id: existing?.id,
  //         sku: edited?.sku || existing?.sku,
  //         itemName: edited.itemName || existing?.itemName || itemName,
  //         cost: String(cost),
  //         systemCount: String(systemCount),
  //         actualCount:
  //           adjustmentType === "Actual Count" ? String(quantity) : undefined,
  //         uom: found?.uom || existing?.uom || {},
  //         lastAdjusted: nowISO,
  //         adjustedBy: { id: user.id },
  //         acl: this.view.getAcl(),
  //         storeId: { id: storeId },
  //         ...(edited?.category && { category: edited.category }), // add if existss
  //       };

  //       const now = new Date(); // current PH time
  //       let businessDate = new Date(now);

  //       if (now.getHours() < 5) {
  //         // if before 5 AM → previous business day
  //         businessDate.setDate(now.getDate() - 1);
  //       }

  //       businessDate = businessDate.toISOString().substring(0, 10);

  //       const statusId = `${storeId}|${businessDate}`;

  //       const payloadStoreStatus = {
  //         id: statusId,
  //         store: {
  //           id: storeId,
  //         },
  //         storeCode: this.userLoggedIn[0]?.storeAnalytic[0]?.name,
  //         businessDate: businessDate,
  //         isActualCountDone: true,
  //       };

  //       const result = await this.upsertUseCase.execute(collection, payload);

  //       if (adjustmentType === "Actual Count") {
  //         await this.upsertUseCase.execute(
  //           "storeInventoryStatus",
  //           payloadStoreStatus,
  //         );
  //       }

  //       // Log & audit
  //       const logEntry = {
  //         user: { id: user.id },
  //         itemName: payload.itemName,
  //         adjustmentType,
  //         oldQuantity: String(currentCount),
  //         newQuantity: String(systemCount),
  //         storeId: { id: storeId },
  //         // reconciledAtOrAfter5AM:
  //         //   adjustmentType === "Actual Count" ? afterFive : undefined,
  //       };
  //       await this.upsertUseCase.execute("inventory_logs", logEntry);

  //       await auditLogUseCase().create({
  //         user: { id: user.id },
  //         logDate: nowISO,
  //         message: `${adjustmentType}: ${result.itemName} from ${String(
  //           currentCount,
  //         )} to ${String(systemCount)}${
  //           adjustmentType === "Actual Count"
  //             ? afterFive
  //               ? " (reconciled)"
  //               : " (pCount only)"
  //             : ""
  //         }`,
  //         collection,
  //         objectId: result.id,
  //         payload: {
  //           before: existing || null,
  //           after: result,
  //         },
  //         store: { id: storeId },
  //       });

  //       editedFinals.set(result.id || existing?.id || itemName, {
  //         systemCount,
  //         actualCount: adjustmentType === "Actual Count" ? quantity : undefined,
  //         itemId: result.id || existing?.id || itemName,
  //         itemName: result.itemName || itemName,
  //         uomName: found?.uom?.name || found?.uom || existing?.uom?.name || "",
  //       });

  //       done += 1;

  //       // update progress label (optional: show current item)
  //       const name = edited?.itemName || found?.itemName || "Item";
  //       this.view.updateSaveProgress(done, `Saving: ${name}`);

  //       // (optional) yield to UI every N items to keep screen responsive
  //       if (done % 20 === 0) {
  //         await new Promise((r) => setTimeout(r, 0));
  //       }
  //     }

  //     if (adjustmentType === "Actual Count") {
  //       // PASS #2 (unchanged except uses new 'systemCount' & pCount from pass #1)
  //       const allInventory = await findObjectUseCase().execute("inventory", {
  //         where: { "storeId.id": { $eq: storeId } },
  //         limit: 10000,
  //       });

  //       const variantUpserts = allInventory.map(async (inv) => {
  //         const invId = inv?.id || inv?.itemName;
  //         const final = editedFinals.get(invId);

  //         const systemCountNum = final
  //           ? Number(final.systemCount)
  //           : Number(inv?.systemCount || 0);

  //         const pCount =
  //           final && typeof final.actualCount === "number"
  //             ? String(final.actualCount)
  //             : undefined;

  //         const uomName = final?.uomName || inv?.uom?.name || inv?.uom || "";

  //         // You can refine these nightly-job fields as you like
  //         const idealNum = Number(pCount || 0) - systemCountNum;
  //         const variantNum = idealNum - Number(inv?.deductionCount || 0);
  //         const costofGoodsNum =
  //           Number(inv?.cost || 0) * Number(inv?.deductionCount || 0);
  //         const saleCountNum = Number(inv?.deductionCount || 0);

  //         const key = `${storeId}|${invId}|${day}`;

  //         return this.upsertUseCase.execute("fc_variant", {
  //           variantId: key,
  //           storeId: { id: storeId, code: storeCode },
  //           item: {
  //             id: invId,
  //             name: final?.itemName || inv?.itemName,
  //             uom: uomName,
  //           },
  //           variantDate: day,
  //           fcProduct: final?.itemName || inv?.itemName,
  //           beginningInventory: String(systemCountNum),
  //           salesCount: String(saleCountNum || 0),
  //           ending: String(systemCountNum),
  //           pCount,
  //           costOfGoods: Number(costofGoodsNum).toFixed(2),
  //           ideal: String(idealNum),
  //           variant: String(variantNum),
  //           movements: {
  //             manualAdd: 0,
  //             manualDeduct: 0,
  //             beginningInventory: 0,
  //             actualAdjustments: 0,
  //           },
  //           physicalCount: pCount,
  //           lastActualCountAt: pCount ? nowISO : undefined,
  //         });
  //       });

  //       await Promise.all(variantUpserts);
  //     }

  //     this.view.hideProgressDialog();

  //     this.view.navigateBack();
  //     dialog.fire({
  //       html: (
  //         <DialogModal
  //           icon="bi bi-question-octagon-fill"
  //           iconColor="#82c14a"
  //           title="Saved Successfully"
  //           message=""
  //           isCancelButton={false}
  //           onSubmit={() => {
  //             dialog.close();
  //           }}
  //           onClose={() => dialog.close()}
  //         />
  //       ),
  //       footer: false,
  //     });
  //   } catch (error) {
  //     console.error("Save error:", error);
  //     this.view.hideProgressDialog();
  //   }
  // }

  onSaveSuccess() {
    this.view.navigateBack();
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Saved Successfully"
          message=""
          isCancelButton={false}
          onSubmit={() => dialog.close()}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  onSaveError(error) {
    console.error("Save error:", error);
  }

  initProgress(total) {
    this.view.showSaveProgress(total, `Saving ${total} items...`);
  }

  async setAsDoneBeginning() {
    const store = this.userLoggedIn[0]?.storeAnalytic[0];

    const payload = {
      id: store?.id,
      isBeginningDone: true,
    };

    await upsertUseCase().execute("storeAnalytics", payload);
  }

  async processInventoryMovement(ctx) {
    // ─── 1. Auto-fetch the latest approved inventory count for this store ──────
    //        Sorted by countDate descending so index [0] is always the most recent
    const [currentCount] = await findObjectUseCase().execute(
      "inventory_counts",
      {
        where: {
          "storeId.id": { $eq: ctx.storeId },
          status: { $eq: "Submitted" },
        },
        sort: { countDate: -1 }, // latest first
        limit: 1,
      },
    );

    if (!currentCount)
      throw new Error("No submitted inventory count found for this store.");

    const periodStart = currentCount.periodStart; // Date – beginning of the period
    const periodEnd = currentCount.periodEnd; // Date – end of the period (= count date)

    // ─── 2. Fetch all inventory items for this store ──────────────────────────
    const allInventory = await findObjectUseCase().execute("inventory", {
      where: { "storeId.id": { $eq: ctx.storeId } },
      limit: 10000,
    });

    // Build a quick SKU → item map
    const inventoryBySku = {};
    for (const item of allInventory) {
      inventoryBySku[item.sku] = item;
    }

    // ─── 3. Fetch beginning inventory ────────────────────────────────────────
    //   Opening count (previousCountId is null):
    //     → use physicalCount from the CURRENT count's own lines (user-inputted)
    //   Regular count (previousCountId exists):
    //     → use physicalCount from the PREVIOUS count's lines
    const isOpeningCount = !currentCount.previousCountId;

    const beginningCountLines = await findObjectUseCase().execute(
      "inventory_count_lines",
      {
        where: {
          countId: {
            $eq: isOpeningCount
              ? currentCount.id
              : currentCount.previousCountId,
          },
          "storeId.id": { $eq: ctx.storeId },
        },
        limit: 10000,
      },
    );

    // Map: sku → beginningQty
    const beginningBySku = {};
    for (const line of beginningCountLines) {
      beginningBySku[line.sku] = parseFloat(line.physicalCount) || 0;
    }

    // ─── 4. Fetch all transactions within the period ──────────────────────────
    //        For Opening counts, there are no transactions yet — skip the fetch.

    console.log(
      "periodStart",
      periodStart,
      "periodEnd",
      periodEnd,
      "ctx.storeId ",
      ctx.storeId,
    );
    // const start = new Date(periodStart);
    // start.setUTCHours(0, 0, 0, 0);

    // const end = new Date(periodEnd);
    // end.setUTCHours(23, 59, 59, 999);

    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const transactions = isOpeningCount
      ? await findObjectUseCase().execute("inventory_transaction", {
          where: {
            "storeId.id": { $eq: ctx.storeId },
            business_date: {
              $gte: start.toISOString(),
              $lte: end.toISOString(),
            },
          },
          limit: 100000,
        })
      : await findObjectUseCase().execute("inventory_transaction", {
          where: {
            "storeId.id": { $eq: ctx.storeId },
            business_date: {
              $gte: start.toISOString(),
              $lte: end.toISOString(),
            },
          },
          limit: 100000,
        });

    const NEGATIVE_TYPES = new Set(["stock_transfer_out", "waste", "sales"]);

    console.log("transactions", transactions);
    const txBySku = {}; // { [sku]: { delivery: 0, direct_purchase: 0, ... } }

    for (const tx of transactions) {
      console.log("text", tx);
      const { sku, transaction_type, quantity } = tx;
      if (!sku) continue;

      console.log("txBySku[sku]", txBySku[sku]);

      if (!txBySku[sku]) {
        txBySku[sku] = {
          beginning: 0,
          delivery: 0,
          direct_purchase: 0,
          stock_transfer_in: 0,
          stock_transfer_out: 0,
          waste: 0,
          sales_count: 0,
        };
      }

      const qty = parseFloat(quantity) || 0;

      switch (transaction_type) {
        case "Beginning Balance":
          txBySku[sku].beginning = qty;
          break;
        case "Actual Count Adjustment":
          txBySku[sku].beginning = qty;
          break;
        case "Receivable Item":
          txBySku[sku].delivery = qty;
          break;
        case "Direct Purchase":
          txBySku[sku].direct_purchase = qty;
          break;
        case "Transfer In":
          txBySku[sku].stock_transfer_in = qty;
          break;
        // Store negatives so the report matches the image (−50, −20, −5 …)
        case "Transfer Out":
          txBySku[sku].stock_transfer_out = qty;
          break;
        case "Waste":
          txBySku[sku].waste = qty;
          break;
        case "Sales Usage":
          txBySku[sku].sales_count = qty;
          break;
      }
    }

    const movementPayloads = allInventory.map((item) => {
      const sku = item.sku;
      const tx = txBySku[sku] || {};

      return {
        // Reference fields
        storeId: { id: ctx.storeId },
        countId: currentCount.id,
        periodStart,
        periodEnd,
        sku,
        fc_product: item.itemName || sku,

        // Movement columns
        beginning_inventory: String(tx.beginning ?? 0),
        delivery: String(tx.delivery ?? 0),
        direct_purchase: String(tx.direct_purchase ?? 0),
        stock_transfer_in: String(tx.stock_transfer_in ?? 0),
        stock_transfer_out: String(tx.stock_transfer_out ?? 0),
        waste: String(tx.waste ?? 0),
        sales_count: String(tx.sales_count ?? 0),
      };
    });

    await this.bulkUpsert("inventory_movement", movementPayloads);

    return { processed: allInventory.length };
  }

  async processInventoryMovementAggregate(ctx) {
    const pipeline = [
      // ── Start from inventory_count_lines of the CURRENT count ──────────────
      {
        $match: {
          "storeId.id": ctx.storeId,
          countId: ctx.countId,
        },
      },

      // ── Lookup the current count to get period boundaries ──────────────────
      {
        $lookup: {
          from: "inventory_counts",
          localField: "countId",
          foreignField: "id",
          as: "currentCount",
        },
      },
      { $unwind: "$currentCount" },

      // ── Lookup PREVIOUS count lines (beginning inventory) ──────────────────
      {
        $lookup: {
          from: "inventory_count_lines",
          let: { prevId: "$currentCount.previousCountId", itemSku: "$sku" },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $eq: ["$countId", "$$prevId"] },
                    { $eq: ["$sku", "$$itemSku"] },
                  ],
                },
              },
            },
          ],
          as: "prevLine",
        },
      },
      {
        $addFields: {
          beginning_inventory: {
            $toDouble: {
              $ifNull: [{ $first: "$prevLine.physicalCount" }, "0"],
            },
          },
        },
      },

      // ── Lookup transactions within the period ─────────────────────────────
      {
        $lookup: {
          from: "inventory_transactions",
          let: {
            itemSku: "$sku",
            storeId: "$storeId",
            periodStart: "$currentCount.periodStart",
            periodEnd: "$currentCount.periodEnd",
          },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $eq: ["$sku", "$$itemSku"] },
                    { $eq: ["$storeId.id", "$$storeId"] },
                    { $gte: ["$business_date", "$$periodStart"] },
                    { $lte: ["$business_date", "$$periodEnd"] },
                  ],
                },
              },
            },
          ],
          as: "transactions",
        },
      },

      // ── Sum each transaction type ─────────────────────────────────────────
      {
        $addFields: {
          delivery: {
            $sum: {
              $map: {
                input: {
                  $filter: {
                    input: "$transactions",
                    cond: { $eq: ["$$this.transaction_type", "delivery"] },
                  },
                },
                in: { $toDouble: "$$this.quantity" },
              },
            },
          },
          direct_purchase: {
            $sum: {
              $map: {
                input: {
                  $filter: {
                    input: "$transactions",
                    cond: {
                      $eq: ["$$this.transaction_type", "direct_purchase"],
                    },
                  },
                },
                in: { $toDouble: "$$this.quantity" },
              },
            },
          },
          stock_transfer_in: {
            $sum: {
              $map: {
                input: {
                  $filter: {
                    input: "$transactions",
                    cond: {
                      $eq: ["$$this.transaction_type", "stock_transfer_in"],
                    },
                  },
                },
                in: { $toDouble: "$$this.quantity" },
              },
            },
          },
          stock_transfer_out: {
            $multiply: [
              -1,
              {
                $sum: {
                  $map: {
                    input: {
                      $filter: {
                        input: "$transactions",
                        cond: {
                          $eq: [
                            "$$this.transaction_type",
                            "stock_transfer_out",
                          ],
                        },
                      },
                    },
                    in: { $toDouble: "$$this.quantity" },
                  },
                },
              },
            ],
          },
          waste: {
            $multiply: [
              -1,
              {
                $sum: {
                  $map: {
                    input: {
                      $filter: {
                        input: "$transactions",
                        cond: { $eq: ["$$this.transaction_type", "waste"] },
                      },
                    },
                    in: { $toDouble: "$$this.quantity" },
                  },
                },
              },
            ],
          },
          sales_count: {
            $multiply: [
              -1,
              {
                $sum: {
                  $map: {
                    input: {
                      $filter: {
                        input: "$transactions",
                        cond: { $eq: ["$$this.transaction_type", "sales"] },
                      },
                    },
                    in: { $toDouble: "$$this.quantity" },
                  },
                },
              },
            ],
          },
        },
      },

      // ── Project final shape ───────────────────────────────────────────────
      {
        $project: {
          _id: 0,
          storeId: "$storeId",
          countId: "$countId",
          sku: 1,
          fc_product: "$inventoryItem",
          periodStart: "$currentCount.periodStart",
          periodEnd: "$currentCount.periodEnd",
          beginning_inventory: 1,
          delivery: 1,
          direct_purchase: 1,
          stock_transfer_in: 1,
          stock_transfer_out: 1,
          waste: 1,
          sales_count: 1,
        },
      },
    ];

    const results = await this.aggregateUseCase.execute(
      "inventory_count_lines",
      pipeline,
    );

    // Upsert each result into inventory_movement
    const jobs = results.map((row) =>
      this.upsertUseCase.execute("inventory_movement", row, {
        where: {
          storeId: { $eq: row.storeId },
          countId: { $eq: row.countId },
          sku: { $eq: row.sku },
        },
      }),
    );

    await Promise.all(jobs);
    return { processed: results.length };
  }

  async generateFCVariant(ctx, editedFinals) {
    const allInventory = await findObjectUseCase().execute("inventory", {
      where: { "storeId.id": { $eq: ctx.storeId } },
      limit: 10000,
    });

    const fcVariantPayloads = allInventory.map((inv) => {
      console.log("inv", inv);
      const invId = inv?.id || inv?.itemName;
      const final = editedFinals.get(invId);

      const pCount =
        final && typeof final.actualCount === "number"
          ? String(final.actualCount)
          : undefined;

      const idealNum =
        Number(pCount || 0) - Number(inv?.systemCountBefore || 0);
      const saleCountNum = Number(inv?.deductionCountPrevious || 0);
      const unitCost = Number(inv?.cost || 0);

      console.log("unitCost", unitCost);

      const adjustedCount =
        idealNum < 0 ? saleCountNum + idealNum : saleCountNum;
      const costofGoodsNum = adjustedCount * unitCost;
      const variance = idealNum * unitCost;
      const beginningInv =
        Number(inv?.systemCountBefore) + Number(inv?.deductionCountPrevious);

      const key = `${ctx.storeId}|${invId}|${ctx.dayISO}`;

      return {
        variantId: key,
        storeId: { id: ctx.storeId },
        item: {
          id: invId,
          name: final?.itemName || inv?.itemName,
          uom: final?.uomName || inv?.uom?.name || "",
        },
        variantDate: ctx.dayISO,
        fcProduct: final?.itemName || inv?.itemName,
        beginningInventory: String(beginningInv || 0),
        salesCount: String(saleCountNum || 0),
        ending: String(inv?.systemCountBefore || 0),
        pCount,
        costOfGoods: Number(costofGoodsNum || 0).toFixed(2),
        ideal: String(idealNum || 0),
        variant: String(variance || 0),
        lastActualCountAt: pCount ? ctx.nowISO : undefined,
      };
    });

    await this.bulkUpsert("fc_variant", fcVariantPayloads);
  }

  async finalizeIfActualCount(ctx, { editedFinals }) {
    if (ctx.adjustmentType !== "Actual Count") return;
    await this.generateFCVariant(ctx, editedFinals);
  }

  async saveCountLines(lines) {
    return Promise.all(
      lines.map((l) => this.upsertUseCase.execute("inventory_count_lines", l)),
    );
  }

  buildCountLine(ctx, countHeader, row, existing, saved, systemQty) {
    const countId = countHeader.id;
    const ideal = Number(systemQty) - Number(row.quantity);
    const variant = ideal * Number(row.cost);
    console.log("saved", saved);
    console.log("existing", existing);
    const itemName =
      saved?.itemName || row?.itemName || existing?.itemName || "";
    console.log("itemName", itemName);
    return {
      countId: countId,
      storeId: { id: ctx.storeId },
      sku: row.sku,
      inventoryItem: itemName,
      beginningCount: String(row.quantity),
      systemQty: String(systemQty),
      physicalCount: String(row.quantity),
      actualQty: String(row.quantity),
      ideal: String(ideal),
      variance: String(variant),
      cost: String(row.cost),
      businessDate: ctx.businessDate,
    };
  }

  async writeAuditLog(ctx, before, after, oldQty, newQty) {
    const payload = {
      user: { id: ctx.user.id },
      logDate: ctx.nowISO,
      message: `${ctx.adjustmentType}: ${after.itemName} from ${String(oldQty)} to ${String(newQty)}`,
      collection: ctx.collection,
      objectId: after.id,
      payload: { before: before || null, after },
      store: { id: ctx.storeId },
    };
    console.log("payload audit", payload);
    return auditLogUseCase().create(payload);
  }

  async writeInventoryLog(ctx, saved, oldQty, newQty, countHeader) {
    const payload = {
      user: { id: ctx.user.id },
      itemName: saved.itemName,
      adjustmentType: ctx.adjustmentType,
      oldQuantity: String(oldQty),
      newQuantity: String(newQty),
      storeId: { id: ctx.storeId },
      reference: countHeader ? { countId: countHeader.countId } : undefined,
    };
    console.log("Payload Log", payload);
    return this.upsertUseCase.execute("inventory_logs", payload);
  }

  async saveBeginningTransaction(ctx, row, existing, computed, currentCount) {
    try {
      const today = new Date().toISOString().split("T")[0];
      const now = new Date().toISOString();

      const isBeginning = ctx.adjustmentType === "Beginning Inventory";

      const payload = {
        storeId: { id: ctx.storeId },
        sku: row.sku,
        item_name: row.itemName || existing?.itemName || "",
        transaction_type: isBeginning
          ? "Beginning Balance"
          : "Actual Count Adjustment",
        quantity: String(computed.systemCount),
        business_date: now,
        reference_id: isBeginning ? `BEGIN-${today}` : `ACTUAL-${today}`,
      };

      return this.upsertUseCase.execute("inventory_transaction", payload);
    } catch (error) {
      throw error;
    }
  }

  async upsertInventoryItem(ctx, row, existing, computed, currentCount) {
    const payload = {
      id: existing?.id,
      sku: row.sku,
      itemName: row.itemName || existing?.itemName,
      cost: String(row.cost),
      deductionCount: String(0),
      deductionCountPrevious: String(existing?.deductionCount),
      systemCount: String(computed.systemCount),
      systemCountBefore: String(currentCount),
      beginningCount:
        ctx.adjustmentType === "Actual Count"
          ? String(row.quantity)
          : undefined,
      actualCount:
        ctx.adjustmentType === "Actual Count"
          ? String(row.quantity)
          : undefined,
      uom: row.uom || existing?.uom || {},
      lastAdjusted: ctx.nowISO,
      adjustedBy: { id: ctx.user.id },
      acl: ctx.acl,
      storeId: { id: ctx.storeId },
      ...(row.category && { category: row.category }),
    };

    return this.upsertUseCase.execute(ctx.collection, payload);
  }

  computeCounts({ adjustmentType, quantity, currentCount, afterFive, is24_7 }) {
    let systemCount = currentCount;
    let actualCount = null;

    if (adjustmentType === "Manual Add")
      return { systemCount: currentCount + quantity, actualCount };
    if (adjustmentType === "Manual Deduct")
      return { systemCount: currentCount - quantity, actualCount };

    if (adjustmentType === "Beginning Inventory")
      return { systemCount: quantity, actualCount };

    if (adjustmentType === "Actual Count") {
      actualCount = quantity;

      console.log("afterFive", afterFive, "is24_7", is24_7);

      // if (afterFive && is24_7) systemCount = actualCount;
      systemCount = actualCount;
      return { systemCount, actualCount };
    }

    return { systemCount, actualCount };
  }

  // Utility: wait for ms milliseconds
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Utility: retry a single call up to maxRetries times on 429
  async retryOnRateLimit(fn, maxRetries = 5, baseDelay = 1000) {
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await fn();
      } catch (error) {
        const is429 =
          error?.status === 429 ||
          error?.statusCode === 429 ||
          error?.message?.includes("429") ||
          error?.message?.toLowerCase().includes("too many requests");

        if (!is429 || attempt === maxRetries - 1) throw error;

        // exponential backoff: 1s, 2s, 4s, 8s, 16s
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(
          `Rate limited (429). Retrying in ${delay}ms... attempt ${attempt + 1}`,
        );
        await this.sleep(delay);
        attempt++;
      }
    }
  }

  // Bulk upsert with rate limit protection
  async bulkUpsert(
    collection,
    payloads,
    { chunkSize = 10, delayBetweenChunks = 600, onProgress = null } = {},
  ) {
    if (!payloads.length) return;

    const chunks = [];
    for (let i = 0; i < payloads.length; i += chunkSize) {
      chunks.push(payloads.slice(i, i + chunkSize));
    }

    let completed = 0;

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      await Promise.all(
        chunk.map((payload) =>
          this.retryOnRateLimit(() =>
            this.upsertUseCase.execute(collection, payload),
          ).then((result) => {
            completed++;
            if (onProgress) onProgress(completed, payloads.length);
            return result;
          }),
        ),
      );

      if (i < chunks.length - 1) {
        await this.sleep(delayBetweenChunks);
      }
    }
  }

  buildInventoryPayload(ctx, row, existing, computed, currentCount) {
    return {
      id: existing?.id,
      sku: row.sku,
      itemName: row.itemName || existing?.itemName,
      cost: String(row.cost),
      deductionCount: String(0),
      deductionCountPrevious: String(existing?.deductionCount),
      systemCount: String(computed.systemCount),
      systemCountBefore: String(currentCount),
      actualCount:
        ctx.adjustmentType === "Actual Count"
          ? String(row.quantity)
          : undefined,
      uom: row.uom || existing?.uom || {},
      lastAdjusted: ctx.nowISO,
      adjustedBy: { id: ctx.user.id },
      acl: ctx.acl,
      storeId: { id: ctx.storeId },
      ...(row.category && { category: row.category }),
    };
  }

  buildTransactionPayload(ctx, row, existing, computed) {
    const today = new Date().toISOString().split("T")[0];
    const isBeginning = ctx.adjustmentType === "Beginning Inventory";
    return {
      storeId: { id: ctx.storeId },
      sku: row.sku,
      item_name: row.itemName || existing?.itemName || "",
      transaction_type: isBeginning
        ? "Beginning Balance"
        : "Actual Count Adjustment",
      quantity: String(computed.systemCount),
      business_date: ctx.nowISO,
      reference_id: isBeginning ? `BEGIN-${today}` : `ACTUAL-${today}`,
    };
  }

  buildLogPayload(ctx, row, existing, computed, currentCount) {
    return {
      user: { id: ctx.user.id },
      itemName: row.itemName || existing?.itemName,
      adjustmentType: ctx.adjustmentType,
      oldQuantity: String(currentCount),
      newQuantity: String(computed.systemCount),
      storeId: { id: ctx.storeId },
    };
  }

  buildAuditPayload(ctx, row, existing, computed, currentCount) {
    return {
      user: { id: ctx.user.id },
      logDate: ctx.nowISO,
      message: `${ctx.adjustmentType}: ${row.itemName} from ${currentCount} to ${computed.systemCount}`,
      collection: ctx.collection,
      payload: { before: existing || null },
      store: { id: ctx.storeId },
    };
  }

  async saveBulkInventoryLog(ctx, rawMaterials) {
    if (!rawMaterials.length) return;

    const payload = {
      adjustmentType: ctx.adjustmentType,
      logDate: ctx.nowISO,
      businessDate: ctx.businessDate,
      adjustedBy: { id: ctx.user.id },
      storeId: { id: ctx.storeId },
      totalItems: rawMaterials.length,
      raw_materials: rawMaterials,
    };

    return this.upsertUseCase.execute("inventory_logs", payload);
  }

  async saveBulkAuditLog(ctx, rawMaterials) {
    if (!rawMaterials.length) return;

    const payload = {
      user: { id: ctx.user.id },
      logDate: ctx.nowISO,
      // businessDate: ctx.businessDate,
      // adjustmentType: ctx.adjustmentType,
      message: `${ctx.adjustmentType} — ${rawMaterials.length} items adjusted`,
      collection: ctx.collection,
      store: { id: ctx.storeId },
      // totalItems: rawMaterials.length,
      raw_materials: rawMaterials,
    };

    return this.upsertUseCase.execute("audit_logs", payload);
  }

  async processRows({ ctx, rows, invBySku, countHeader }) {
    const editedFinals = new Map();
    const rawMaterialsLog = [];
    const rawMaterialsAudit = [];
    const inventoryPayloads = [];
    const transactionPayloads = [];
    const countLines = [];

    // build all payloads in memory
    for (const row of rows) {
      const existing = invBySku.get(row.sku);
      const currentCount = toNumber(existing?.systemCount);

      const computed = this.computeCounts({
        adjustmentType: ctx.adjustmentType,
        quantity: row.quantity,
        currentCount,
        afterFive: ctx.afterFive,
        is24_7: ctx.is24_7,
      });

      inventoryPayloads.push(
        this.buildInventoryPayload(ctx, row, existing, computed, currentCount),
      );
      transactionPayloads.push(
        this.buildTransactionPayload(ctx, row, existing, computed),
      );
      countLines.push(
        this.buildCountLine(
          ctx,
          countHeader,
          row,
          existing,
          null,
          currentCount,
        ),
      );

      rawMaterialsLog.push({
        sku: row.sku,
        itemName: row.itemName || existing?.itemName,
        oldQuantity: String(currentCount),
        newQuantity: String(computed.systemCount),
        uom: row?.uom?.name || existing?.uom?.name || "",
      });

      rawMaterialsAudit.push({
        sku: row.sku,
        itemName: row.itemName || existing?.itemName,
        // before: existing || null,
        systemCountBefore: String(currentCount),
        systemCountAfter: String(computed.systemCount),
      });

      editedFinals.set(row.id || row.itemName, {
        systemCount: computed.systemCount,
        actualCount:
          ctx.adjustmentType === "Actual Count" ? row.quantity : undefined,
        itemName: row.itemName,
        uomName: row?.uom?.name || existing?.uom?.name || "",
      });
    }

    // total trackable writes = inventory + transaction + countLines
    // logs and audit are just 1 call each so we count them as a fixed step
    const totalItems = rows.length;
    let globalCompleted = 0;

    const trackProgress = (label) => (completed, total) => {
      globalCompleted++;
      const percent = Math.round((globalCompleted / (totalItems * 3)) * 100);
      this.view.updateSaveProgress(globalCompleted, `${label} — ${percent}%`);
    };

    // save one collection at a time to avoid 429
    await this.bulkUpsert(ctx.collection, inventoryPayloads, {
      onProgress: trackProgress("Saving inventory"),
    });

    // await this.bulkUpsert("inventory_transaction", transactionPayloads, {
    //   onProgress: trackProgress("Saving transactions"),
    // });

    await this.bulkUpsert("inventory_count_lines", countLines, {
      onProgress: trackProgress("Saving count lines"),
    });

    this.view.updateSaveProgress(totalItems * 3, "Saving logs...");
    await this.saveBulkInventoryLog(ctx, rawMaterialsLog);

    this.view.updateSaveProgress(totalItems * 3, "Saving audit...");
    await this.saveBulkAuditLog(ctx, rawMaterialsAudit);

    return { editedFinals };
  }

  async saveStoreInventoryStatus(ctx) {
    return this.upsertUseCase.execute("storeInventoryStatus", {
      store: { id: ctx.storeId },
      storeCode: ctx.storeCode,
      businessDate: ctx.businessDate,
      isActualCountDone: true,
    });
  }

  async findLatestPreviousCount({ storeCode }) {
    const rows = await findObjectUseCase().execute("inventory_counts", {
      where: {
        storeCode: storeCode,
      },
      sort: { countDate: -1 },
      limit: 1,
    });

    return rows?.[0] || null;
  }

  async ensureCountHeaderIfNeeded(ctx) {
    // if (ctx.adjustmentType !== "Actual Count") return null;

    const today = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const shortUUID = crypto.randomUUID().split("-")[0].toUpperCase();
    const countId = `COUNT-${ctx.storeCode}-${today}-${shortUUID}`;

    const existingCounts = await this.findLatestPreviousCount({
      storeCode: ctx.storeCode,
    });

    const previousCountId = existingCounts?.id || "";
    const previousCountDate = existingCounts?.countDate;

    const header = await this.upsertUseCase.execute("inventory_counts", {
      id: countId,
      storeId: { id: ctx.storeId },
      countDate: new Date().toISOString(),
      previousCountId: previousCountId,
      periodStart: previousCountDate || undefined,
      periodEnd: new Date().toISOString(),
      count_type:
        ctx.adjustmentType === "Beginning Inventory" ? "Opening" : "Regular",
      countedBy: { id: ctx.user.id },
      storeCode: ctx.storeCode,
      status: "Submitted",
    });

    // await this.saveStoreInventoryStatus(ctx);
    return { ...header, countId };
  }

  async preloadInventoryBySku(storeId) {
    const list = await findObjectUseCase().execute("inventory", {
      where: { "storeId.id": { $eq: storeId } },
      limit: 10000,
    });
    // console.log("list", list);
    const map = new Map();
    list.forEach((inv) => map.set(inv.sku, inv));
    return map;
  }

  loadEditedRows(ctx) {
    const ids = Object.keys(ctx.editedItems);
    return ids.map((id) => {
      const edited = ctx.editedItems[id];
      const found = ctx.infiniteItems.find((x) => x.id === id);

      return {
        id,
        sku: edited?.sku || found?.sku,
        itemName: edited?.itemName || found?.itemName || "",
        cost: toNumber(edited?.cost ?? found?.cost ?? 0),
        quantity: toNumber(edited?.quantity),
        category: edited?.category,
        uom: found?.uom,
        found,
        edited,
      };
    });
  }

  validateContext(ctx) {
    if (!ctx.user?.id) throw new Error("Missing user");
    if (!ctx.storeId) throw new Error("Missing storeId");
  }

  async resolveActualCountBusinessDate({ storeCode, now }) {
    const today = toISODate(now);
    const yesterday = addDaysISO(today, -1);

    const [last] = await findObjectUseCase().execute("storeInventoryStatus", {
      where: { storeCode: storeCode, isActualCountDone: true },
      sort: { businessDate: -1 },
      limit: 1,
    });

    const lastClosed = last?.businessDate ? toISODate(last.businessDate) : null;
    const nextToClose = lastClosed ? addDaysISO(lastClosed, 1) : yesterday;
    if (nextToClose < today) return nextToClose;
    return today;
  }

  async getContext() {
    const now = new Date();
    const nowISO = now.toISOString();
    const user = this.view.getCurrentUser();
    const storeAnalytic = user?.storeAnalytic?.[0];
    const operationType =
      this.userLoggedIn[0]?.storeAnalytic?.[0]?.operationType;
    const is24_7 = operationType === "24/7 Store";
    const afterFive = isAfterFiveAMManila(now);
    const storeId = storeAnalytic?.id;
    const storeCode = this.userLoggedIn[0]?.storeAnalytic?.[0]?.name;
    const adjustmentType = this.view.getAdjustmentType();

    let businessDate;
    if (adjustmentType === "Actual Count") {
      businessDate = await this.resolveActualCountBusinessDate({
        storeCode,
        now,
      });
    } else {
      businessDate = getBusinessDate(now);
    }

    return {
      user,
      storeAnalytic,
      storeId,
      storeCode,
      now,
      nowISO,
      dayISO: nowISO.slice(0, 10),
      businessDate,
      collection: this.view.getCollectionName(),
      adjustmentType: this.view.getAdjustmentType(),
      acl: this.view.getAcl(),
      afterFive,
      is24_7,
      editedItems: this.view.state.selectedQuantities || {},
      infiniteItems: this.view.state.infiniteItems || [],
    };
  }

  async save() {
    this.view.showProgressDialog();

    try {
      const adjustmentType = this.view.getAdjustmentType();
      const ctx = await this.getContext();
      this.validateContext(ctx);
      const rows = this.loadEditedRows(ctx);
      console.log("loadEditedRows", rows);
      this.initProgress(rows.length);
      const invBySku = await this.preloadInventoryBySku(ctx.storeId);
      console.log("preloadInventoryBySku", invBySku);
      const countHeader = await this.ensureCountHeaderIfNeeded(ctx);
      const results = await this.processRows({
        ctx,
        rows,
        invBySku,
        countHeader,
      });

      await this.finalizeIfActualCount(ctx, results);

      if (adjustmentType === "Actual Count") {
        await this.processInventoryMovement(ctx);
      }

      if (adjustmentType === "Beginning Inventory") {
        await this.setAsDoneBeginning();
      }

      this.view.hideProgressDialog();
      this.onSaveSuccess();
    } catch (error) {
      this.onSaveError(error);
      console.log("err", error);
    } finally {
      this.view.hideProgressDialog();
    }
  }

  async submit(adjustmentType) {
    try {
      this.view.submitting();
      dialog.close();
      if (adjustmentType) {
        await this.saveActual();
      } else {
        await this.save();
      }
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default InventoryFormPresenter;
